import React from 'react';
import { Button as MuiButton, styled } from '@mui/material';

/**
 * Universal Button component for the UI kit.
 * Compatible with the MUI Button API with enhanced styling.
 *
 * Variants:
 * - contained: Filled button with gradient for primary color
 * - outlined: Bordered button with hover effects
 * - text: Text-only button with subtle hover
 * - neutral: Neutral button with primary text color
 *
 * Colors: primary, secondary, error, warning, info, success
 *
 * Example usage:
 *
 * // Primary contained button (default)
 * <Button variant="contained" color="primary">
 *   Save Changes
 * </Button>
 *
 * // Outlined button
 * <Button variant="outlined" color="secondary">
 *   Cancel
 * </Button>
 *
 * // Text button
 * <Button variant="text" color="primary">
 *   Learn More
 * </Button>
 *
 * // Neutral button
 * <Button variant="neutral">
 *   Neutral Action
 * </Button>
 *
 * // Disabled state
 * <Button disabled>
 *   Disabled Button
 * </Button>
 *
 * All additional props and styling are supported as in MUI Button.
 */

// Современный стиль кнопки
const StyledButton = styled(MuiButton)(
  ({ theme, variant = 'contained', color = 'primary' }) => ({
    borderRadius: '10px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.95rem',
    letterSpacing: '0.01em',
    boxShadow: 'none',
    transition: 'all 0.25s ease',

    // Стили для основной кнопки
    ...(variant === 'contained' && {
      color: theme.palette.common.white,
      // Используем градиент только для primary цвета
      ...(color === 'primary' && {
        background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette[color].main} 100%)`,
        '&:hover': {
          background: `linear-gradient(90deg, ${theme.palette.success.dark} 0%, ${theme.palette[color].dark} 100%)`,
          boxShadow: `0 5px 15px ${theme.palette[color].main}33`,
        },
      }),
      // Для других цветов используем стандартный фон
      ...(color !== 'primary' && {
        background: theme.palette[color].main,
        '&:hover': {
          background: theme.palette[color].dark,
          boxShadow: `0 5px 15px ${theme.palette[color].main}33`,
        },
      }),
    }),

    // Стили для обрамленной кнопки
    ...(variant === 'outlined' && {
      borderColor: theme.palette[color].main,
      borderWidth: '2px',
      color: theme.palette[color].main,
      '&:hover': {
        borderColor: theme.palette[color].dark,
        background: `${theme.palette[color].main}0a`,
      },
    }),

    // Стили для текстовой кнопки
    ...(variant === 'text' && {
      color: theme.palette[color].main,
      '&:hover': {
        background: `${theme.palette[color].main}0a`,
      },
    }),

    // Стили для нейтральной кнопки
    ...(variant === 'neutral' && {
      color: theme.palette.text.primary,
      borderColor: theme.palette.text.primary,
      '&:hover': {
        borderColor: theme.palette.text.primary,
        backgroundColor: theme.palette.action.hover,
      },
    }),

    '&.Mui-disabled': {
      backgroundImage: 'none',
      color: theme.palette.action.disabled,
      ...(variant === 'contained' && {
        background: theme.palette.action.hover,
        color: theme.palette.text.disabled,
      }),
    },
  })
);

const Button = (props) => {
  return <StyledButton {...props} />;
};

export default Button;
